import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCartShopping, faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import GroceryCardPrice, { GroceryCardPackQty } from './GroceryCardPrice';
import SearchableSelect from './SearchableSelect';
import {
  BUY_GROCERY_CART_PATH,
  cartCountFromCart,
  getBuyGroceryBuyerContext,
  getGroceryItemStockLimit,
  loadBuyGroceryCart,
  saveBuyGroceryCart,
  validateGroceryCartQuantity,
} from './buyGroceryCartStorage';
import {
  fetchGroceryCatalogForStore,
  fetchNearbyGroceryStores,
  fetchNearbyGroceryStoresForStore,
  getGroceryImageUrl,
} from './groceryApi';
import './css/BuyGrocery.css';
import './css/PlaceOrder.css';

const PURCHASES_PATH = '/home?tab=purchase';
const NEARBY_RADIUS_KM = 5;

const BuyGrocery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const buyer = useMemo(() => getBuyGroceryBuyerContext(), [location.pathname]);
  const buyerId = buyer.buyerId;

  const [stores, setStores] = useState([]);
  const [nearbyMeta, setNearbyMeta] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [itemsError, setItemsError] = useState('');
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [cart, setCart] = useState(() => loadBuyGroceryCart(buyer));

  useEffect(() => {
    setCart(loadBuyGroceryCart(buyer));
  }, [location.pathname, buyer, buyerId]);

  useEffect(() => {
    saveBuyGroceryCart(buyer, cart);
  }, [cart, buyer, buyerId]);

  const goBack = () => {
    navigate(PURCHASES_PATH);
  };

  const loadNearbyStores = useCallback(async () => {
    if (!buyerId) {
      setStores([]);
      setNearbyMeta(null);
      setLoadError('');
      return;
    }

    setStoresLoading(true);
    setLoadError('');
    try {
      const data = buyer.isStore
        ? await fetchNearbyGroceryStoresForStore(buyerId, { radiusKm: NEARBY_RADIUS_KM })
        : await fetchNearbyGroceryStores(buyerId, { radiusKm: NEARBY_RADIUS_KM });
      setStores(Array.isArray(data.stores) ? data.stores : []);
      if (buyer.isStore) {
        setNearbyMeta({
          storeId: data.storeId,
          storeName: data.storeName,
          radiusKm: data.radiusKm,
          count: data.count,
        });
      } else {
        setNearbyMeta({
          restaurantId: data.restaurantId,
          restaurantName: data.restaurantName,
          radiusKm: data.radiusKm,
          count: data.count,
        });
      }
    } catch (err) {
      setStores([]);
      setNearbyMeta(null);
      setLoadError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to load nearby grocery stores.'
      );
    } finally {
      setStoresLoading(false);
    }
  }, [buyer.isStore, buyerId]);

  const loadStoreItems = useCallback(async (store) => {
    if (!store?.supermarketId) return;

    setItemsLoading(true);
    setItemsError('');
    setCategories([]);
    setItems([]);
    try {
      const data = await fetchGroceryCatalogForStore(store.supermarketId, store);
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setCategories([]);
      setItems([]);
      setItemsError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to load grocery items for this store.'
      );
    } finally {
      setItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedStore(null);
    setStoreSearchQuery('');
    setSearchQuery('');
    setActiveCategory('all');
    loadNearbyStores();
  }, [loadNearbyStores]);

  const handleSelectStore = (store) => {
    setSelectedStore(store);
    setSearchQuery('');
    setActiveCategory('all');
    setItemsError('');
    loadStoreItems(store);
  };

  const handleBackFromStore = () => {
    setSelectedStore(null);
    setSearchQuery('');
    setActiveCategory('all');
    setItems([]);
    setCategories([]);
    setItemsError('');
  };

  const handleHeaderBack = () => {
    if (selectedStore) {
      handleBackFromStore();
      return;
    }
    goBack();
  };

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        id: String(c.groceryCategoryId),
        name: c.categoryName || 'Category',
      })),
    [categories]
  );

  const categorySelectOptions = useMemo(
    () => [
      { value: 'all', label: 'All categories' },
      ...categoryOptions.map((cat) => ({ value: cat.id, label: cat.name })),
    ],
    [categoryOptions]
  );

  const filteredStores = useMemo(() => {
    const q = storeSearchQuery.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((store) => {
      const haystack = [
        store.supermarketName,
        store.city,
        store.shortCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [stores, storeSearchQuery]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const matchesCategory =
        activeCategory === 'all' || String(item.groceryCategoryId) === String(activeCategory);
      if (!matchesCategory) return false;
      if (!q) return true;
      const haystack = [item.itemName, item.barcode, item.groceryCategoryName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, searchQuery, activeCategory]);

  const cartCount = useMemo(() => cartCountFromCart(cart), [cart]);

  const addToCart = (item) => {
    if (!buyerId) return;
    const key = String(item.groceryItemId);
    const stockLimit = getGroceryItemStockLimit(item);
    setCart((prev) => {
      const currentQty = prev[key]?.quantity ?? 0;
      const nextQty = currentQty + 1;
      if (!validateGroceryCartQuantity(item.itemName, nextQty, stockLimit)) {
        return prev;
      }
      return {
        ...prev,
        [key]: {
          groceryItemId: item.groceryItemId,
          groceryId: item.groceryId ?? item.groceryProductId ?? null,
          itemName: item.itemName,
          unitPrice: Number(item.sellingPrice) || 0,
          supermarketId: item.supermarketId,
          supermarketName: item.supermarketName || '',
          stockQuantity: stockLimit,
          quantity: nextQty,
        },
      };
    });
  };

  const updateQuantity = (item, nextQty) => {
    const groceryItemId = item?.groceryItemId ?? item;
    const key = String(groceryItemId);
    const stockLimit = getGroceryItemStockLimit(item);
    const itemName = item?.itemName ?? cart[key]?.itemName;
    if (nextQty <= 0) {
      setCart((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    if (!validateGroceryCartQuantity(itemName, nextQty, stockLimit)) {
      return;
    }
    setCart((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        stockQuantity: stockLimit ?? prev[key]?.stockQuantity ?? null,
        quantity: nextQty,
      },
    }));
  };

  const pageTitle = selectedStore
    ? selectedStore.supermarketName || 'Store'
    : 'Buy Groceries';

  const buyerRequiredMessage = buyer.isStore
    ? 'Select a store to buy groceries.'
    : 'Select a restaurant to buy groceries.';

  return (
    <RestaurantDashboardLayout activeTab="purchase">
      <div className="buy-grocery-page">
        <div className="buy-grocery-page-header buy-grocery-page-header--with-cart">
          <div className="buy-grocery-page-header-left">
            <button
              type="button"
              className="buy-grocery-page-back"
              onClick={handleHeaderBack}
              aria-label={selectedStore ? 'Back to stores' : 'Back to purchases'}
            >
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden />
            </button>
            <h1 className="buy-grocery-page-title">{pageTitle}</h1>
          </div>
          <div className="buy-grocery-header-cart-wrap">
            {cartCount > 0 ? (
              <span className="buy-grocery-header-cart-badge" aria-hidden>
                {cartCount}
              </span>
            ) : null}
            <button
              type="button"
              className="buy-grocery-header-cart-btn"
              onClick={() => navigate(BUY_GROCERY_CART_PATH)}
              disabled={cartCount <= 0}
              aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart, empty'}
            >
              <FontAwesomeIcon icon={faCartShopping} aria-hidden />
            </button>
          </div>
        </div>

        {!buyerId ? <p className="buy-grocery-status">{buyerRequiredMessage}</p> : null}

        {loadError ? <p className="buy-grocery-status">{loadError}</p> : null}
        {itemsError ? <p className="buy-grocery-status">{itemsError}</p> : null}

        {!selectedStore ? (
          <>
            <div className="buy-grocery-toolbar">
              <div className="search-container search-container-pos buy-grocery-store-search">
                <FontAwesomeIcon icon={faSearch} className="search-icon-menu" aria-hidden />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search stores..."
                  value={storeSearchQuery}
                  onChange={(e) => setStoreSearchQuery(e.target.value)}
                  aria-label="Search stores"
                />
                {storeSearchQuery.trim() !== '' && (
                  <button
                    type="button"
                    className="pos-inline-search-clear"
                    onClick={() => setStoreSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            </div>

            {storesLoading ? <p className="buy-grocery-loading">Loading stores…</p> : null}

            {!storesLoading && !loadError && filteredStores.length === 0 ? (
              <p className="buy-grocery-empty">
                {nearbyMeta && nearbyMeta.count === 0
                  ? `No grocery stores within ${nearbyMeta.radiusKm ?? NEARBY_RADIUS_KM} km.`
                  : 'No stores match your search.'}
              </p>
            ) : null}

            {!storesLoading && filteredStores.length > 0 ? (
              <div className="buy-grocery-stores-grid">
                {filteredStores.map((store) => (
                  <button
                    key={store.supermarketId}
                    type="button"
                    className="buy-grocery-store-card"
                    onClick={() => handleSelectStore(store)}
                  >
                    <div className="buy-grocery-store-card-icon" aria-hidden>
                      🏪
                    </div>
                    <div className="buy-grocery-store-card-body">
                      <h2 className="buy-grocery-store-card-name">
                        {store.supermarketName || `Store ${store.supermarketId}`}
                      </h2>
                      <p className="buy-grocery-store-card-meta">
                        {[
                          store.city,
                          store.distanceKm != null ? `${store.distanceKm} km` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </p>
                      {store.homeDeliveryAvailable ? (
                        <span className="buy-grocery-store-card-badge">Home delivery</span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="buy-grocery-toolbar">
              <div className="content-header-actions pos-search-category-row buy-grocery-search-category-row">
                <div className="search-container search-container-pos">
                  <FontAwesomeIcon icon={faSearch} className="search-icon-menu" aria-hidden />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search grocery items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search grocery items"
                  />
                  {searchQuery.trim() !== '' && (
                    <button
                      type="button"
                      className="pos-inline-search-clear"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
                <div className="pos-category-select-wrap">
                  <label className="pos-category-select-label" htmlFor="buy-grocery-category-select">
                    Category
                  </label>
                  <SearchableSelect
                    id="buy-grocery-category-select"
                    value={activeCategory}
                    onChange={(val) => {
                      setActiveCategory(val);
                      setSearchQuery('');
                    }}
                    options={categorySelectOptions}
                    placeholder="All categories"
                    searchPlaceholder="Search categories"
                    className="pos-category-searchable-select"
                    triggerClassName="pos-category-select-trigger"
                    minOptionsForSearch={1}
                    emptyMessage="No categories found"
                  />
                </div>
              </div>
            </div>

            {itemsLoading ? <p className="buy-grocery-loading">Loading groceries…</p> : null}

            {!itemsLoading && !itemsError && filteredItems.length === 0 ? (
              <p className="buy-grocery-empty">No grocery items found in this store.</p>
            ) : (
              <div className="buy-grocery-grid">
                {filteredItems.map((item) => {
                  const imageUrl = getGroceryImageUrl(item);
                  const inCart = cart[String(item.groceryItemId)]?.quantity ?? 0;
                  const stockLimit = getGroceryItemStockLimit(item);
                  const atStockLimit =
                    stockLimit != null && inCart >= stockLimit;
                  return (
                    <article
                      key={item.groceryItemId}
                      className={`buy-grocery-card${inCart > 0 ? ' is-in-cart' : ''}`}
                    >
                      <div className="buy-grocery-card-image-wrap">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.itemName || 'Grocery item'}
                            className="buy-grocery-card-image"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="buy-grocery-card-placeholder" aria-hidden>
                            🧺
                          </span>
                        )}
                      </div>
                      <div className="buy-grocery-card-body">
                        <h2 className="buy-grocery-card-name-row">
                          <span className="buy-grocery-card-name">{item.itemName || 'Item'}</span>
                          <GroceryCardPackQty item={item} />
                        </h2>
                        <div className="buy-grocery-card-footer">
                          <GroceryCardPrice item={item} />
                          <div className="buy-grocery-card-action">
                            {inCart > 0 ? (
                              <div className="buy-grocery-pos-qty">
                                <button
                                  type="button"
                                  className="buy-grocery-pos-qty-btn"
                                  onClick={() => updateQuantity(item, inCart - 1)}
                                  aria-label="Decrease quantity"
                                >
                                  −
                                </button>
                                <span className="buy-grocery-pos-qty-count">{inCart}</span>
                                <button
                                  type="button"
                                  className="buy-grocery-pos-qty-btn"
                                  onClick={() => addToCart(item)}
                                  aria-label="Increase quantity"
                                  disabled={!buyerId || atStockLimit}
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="buy-grocery-card-add"
                                onClick={() => addToCart(item)}
                                disabled={!buyerId || (stockLimit != null && stockLimit <= 0)}
                              >
                                Add
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </RestaurantDashboardLayout>
  );
};

export default BuyGrocery;
